/**
 * ⚖️ DISPUTE SERVICE - Manage dispute cases and resolutions
 */

import { getApiBaseUrl } from '@/config/environment';


export interface DisputeUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  type: 'host' | 'renter' | 'driver';
}

export interface Dispute {
  id: string;
  booking_id: string;
  booking_ref?: string;
  type: 'damage' | 'payment' | 'cancellation' | 'substitution' | 'late_return' | 'cleanliness' | 'driver_conduct' | 'other';
  status: 'pending' | 'investigating' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  description: string;
  amount?: number;
  refund_amount?: number;
  filed_by_id: string;
  filed_by?: DisputeUser;
  filed_against_id: string;
  filed_against?: DisputeUser;
  assigned_to_id?: string;
  assigned_to?: {
    id: string;
    name: string;
    email: string;
  };
  evidence_urls?: string[];
  internal_notes?: Array<{
    note: string;
    added_by: string;
    added_at: string;
  }>;
  resolution?: string;
  resolution_notes?: string;
  resolved_at?: string;
  resolved_by_id?: string;
  created_at: string;
  updated_at: string;
  vehicle?: {
    id: string;
    make: string;
    model: string;
    year: number;
    plate_number?: string;
  };
  activity?: DisputeActivity[];
}

export interface DisputeActivity {
  id: string;
  dispute_id: string;
  action_type: 'created' | 'status_change' | 'assigned' | 'note_added' | 'resolved' | 'updated';
  description: string;
  performed_by?: string;
  metadata?: any;
  created_at: string;
}

export interface DisputeStatistics {
  total: number;
  pending: number;
  investigating: number;
  resolved: number;
  closed: number;
  unassigned: number;
  by_type: {
    damage: number;
    payment: number;
    cancellation: number;
    substitution: number;
    late_return: number;
    cleanliness: number;
    driver_conduct: number;
    other: number;
  };
  by_priority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
}

export interface DisputeFilters {
  status?: 'pending' | 'investigating' | 'resolved' | 'closed';
  type?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface DisputeListResponse {
  success: boolean;
  data: {
    disputes: Dispute[];
    statistics: DisputeStatistics;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  timestamp: string;
}

export interface DisputeDetailsResponse {
  success: boolean;
  data: {
    dispute: Dispute;
    activity: DisputeActivity[];
  };
  timestamp: string;
}

export const disputeService = {
  /**
   * 📋 Get all disputes with filters
   */
  async getDisputes(filters?: DisputeFilters): Promise<{
    success: boolean;
    data?: {
      disputes: Dispute[];
      statistics: DisputeStatistics;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {

      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: DisputeListResponse = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching disputes:', error);

      // Return empty data structure instead of error to prevent UI crash
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch disputes',
        data: {
          disputes: [],
          statistics: {
            total: 0,
            pending: 0,
            investigating: 0,
            resolved: 0,
            closed: 0,
            unassigned: 0,
            by_type: {
              damage: 0,
              payment: 0,
              cancellation: 0,
              substitution: 0,
              late_return: 0,
              cleanliness: 0,
              driver_conduct: 0,
              other: 0
            },
            by_priority: {
              low: 0,
              medium: 0,
              high: 0,
              urgent: 0
            }
          },
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0
          }
        }
      };
    }
  },

  /**
   * 📄 Get dispute details
   */
  async getDisputeDetails(id: string): Promise<{
    success: boolean;
    data?: {
      dispute: Dispute;
      activity: DisputeActivity[];
    };
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Backend returns dispute directly in data, not wrapped in data.dispute
      // and returns timeline instead of activity
      return {
        success: true,
        data: {
          dispute: result.data as Dispute,
          activity: (result.data.timeline || []) as DisputeActivity[]
        }
      };
    } catch (error) {
      console.error('❌ Error fetching dispute details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dispute details'
      };
    }
  },

  /**
   * ✏️ Update dispute
   */
  async updateDispute(id: string, updates: {
    title?: string;
    description?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    type?: string;
    status?: 'pending' | 'investigating' | 'resolved' | 'closed';
    amount?: number;
  }): Promise<{
    success: boolean;
    data?: Dispute;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data.dispute
      };
    } catch (error) {
      console.error('❌ Error updating dispute:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update dispute'
      };
    }
  },

  /**
   * 👤 Assign dispute to admin
   */
  async assignDispute(id: string, data: {
    assignedToId: string;
    notes?: string;
  }): Promise<{
    success: boolean;
    data?: Dispute;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes/${id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assigned_to_id: data.assignedToId,
          notes: data.notes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data.dispute
      };
    } catch (error) {
      console.error('❌ Error assigning dispute:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to assign dispute'
      };
    }
  },

  /**
   * ✅ Resolve dispute
   */
  async resolveDispute(id: string, data: {
    resolution: string;
    resolutionNotes?: string;
    refundAmount?: number;
    closeDispute?: boolean;
  }): Promise<{
    success: boolean;
    data?: Dispute;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes/${id}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resolution: data.resolution,
          resolution_notes: data.resolutionNotes,
          refund_amount: data.refundAmount,
          close_dispute: data.closeDispute
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data.dispute
      };
    } catch (error) {
      console.error('❌ Error resolving dispute:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve dispute'
      };
    }
  },

  /**
   * 📝 Add internal note to dispute
   */
  async addNote(id: string, note: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {

      // Note: This would be added through the update endpoint or a dedicated notes endpoint
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          note
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }


      return {
        success: true
      };
    } catch (error) {
      console.error('❌ Error adding note:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add note'
      };
    }
  },

  /**
   * ➕ Create new dispute case
   */
  async createDispute(data: {
    booking_id: string;
    type: 'damage' | 'payment' | 'cancellation' | 'substitution' | 'late_return' | 'cleanliness' | 'driver_conduct' | 'other';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    title: string;
    description: string;
    amount?: number;
    currency?: string;
    filed_by_id: string;
    filed_against_id: string;
    evidence_urls?: string[];
  }): Promise<{
    success: boolean;
    data?: Dispute;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Dispute Error ${response.status}:`, errorText);
        throw new Error(`Failed to create dispute (${response.status})`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error creating dispute:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create dispute'
      };
    }
  },

  /**
   * 🔐 Get verification code details for booking
   */
  async getVerificationDetails(bookingId: string): Promise<{
    success: boolean;
    data?: {
      bookingId: string;
      code: string;
      generatedAt: string;
      expiresAt: string;
      isVerified: boolean;
      verifiedAt: string | null;
      verifiedBy: string | null;
      attempts: number;
      failedAttempts: number;
      isLocked: boolean;
      lockedUntil: string | null;
    };
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/verification`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching verification details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch verification details'
      };
    }
  },

  /**
   * 🔄 Regenerate verification code
   */
  async regenerateVerificationCode(bookingId: string): Promise<{
    success: boolean;
    data?: { code: string };
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/verification/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error regenerating verification code:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to regenerate verification code'
      };
    }
  },

  /**
   * 📸 Get check-in/check-out details for booking
   */
  async getCheckinCheckoutDetails(bookingId: string): Promise<{
    success: boolean;
    data?: {
      booking: {
        id: string;
        bookingNumber: string;
        renterName: string;
        hostName: string;
        vehicleName: string;
      };
      checkin: {
        completedAt: string | null;
        renter: {
          confirmedAt: string | null;
          photos: string[];
          mileage: number | null;
          fuelLevel: number | null;
          damageReport: string | null;
          notes: string | null;
        };
        host: {
          confirmedAt: string | null;
          photos: string[];
          damageReport: string | null;
          notes: string | null;
        };
        hasDispute: boolean;
      };
      checkout: {
        completedAt: string | null;
        renter: {
          confirmedAt: string | null;
          photos: string[];
          mileage: number | null;
          fuelLevel: number | null;
          damageReport: string | null;
          notes: string | null;
        };
        host: {
          confirmedAt: string | null;
          photos: string[];
          damageReport: string | null;
          notes: string | null;
        };
        hasDispute: boolean;
      };
      dispute: {
        type: string;
        raisedBy: string;
        reason: string;
        raisedAt: string;
        status: string;
      } | null;
    };
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/checkin-checkout`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching check-in/check-out details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch check-in/check-out details'
      };
    }
  },

  /**
   * ⚖️ Resolve check-in/check-out dispute
   */
  async resolveCheckinCheckoutDispute(bookingId: string, data: {
    decision: 'side_with_host' | 'side_with_renter' | 'split_responsibility';
    resolution: string;
    chargeAmount?: number;
    refundAmount?: number;
  }): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/dispute/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error resolving dispute:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve dispute'
      };
    }
  }
};
